/**
 * `AW2` · `AW3` — la cáscara de un diálogo modal. Sin lógica de negocio.
 *
 * Existe para que `modal-carga.tsx` y `acciones.tsx` no repitan la accesibilidad
 * de un modal, y para que las decisiones con `guard` de esos dos archivos queden
 * a la vista sin cien líneas de `role` y `aria-` alrededor.
 *
 * **Se usa un `<div role="dialog">` y no el `<dialog>` del navegador.** El
 * elemento nativo es mejor en casi todo —foco, `::backdrop`, capa superior— y no
 * se usa por una razón concreta: `showModal()` sólo se puede llamar por efecto
 * después del montaje, así que el diálogo existe un frame en el DOM **sin ser
 * modal**, y esa ventana es justo donde queda el foco cuando el modal se abre
 * por teclado. Con `role="dialog"` + `aria-modal` el estado no tiene ese
 * intermedio.
 *
 * **No cierra al hacer clic en el fondo.** En el modal de carga eso significaría
 * perder una selección de archivos con un clic al costado, y en el de borrado
 * significaría que el gesto más descuidado posible está a un pixel de la
 * confirmación. Se cierra con «Cancelar» o con Escape, que son deliberados.
 */

import { useEffect, useRef } from "react"
import type { ReactNode } from "react"

export function Dialogo({
  titulo,
  onCerrar,
  children,
}: {
  readonly titulo: string
  readonly onCerrar: () => void
  readonly children: ReactNode
}) {
  const contenedor = useRef<HTMLDivElement>(null)

  /**
   * El foco entra al diálogo al abrirse.
   *
   * Sin esto el foco se queda en el botón que lo abrió, detrás del overlay: quien
   * navega con teclado abre el modal y sigue tabulando por la tabla de atrás sin
   * ver dónde está.
   */
  useEffect(() => {
    contenedor.current?.focus()
  }, [])

  /**
   * ⭐ EL FOCO VUELVE AL BOTÓN QUE ABRIÓ EL DIÁLOGO.
   *
   * Sin esto, cerrar el modal deja el foco en el `<body>`: quien lo abrió con
   * teclado vuelve al principio del documento y tiene que recorrer las siete
   * áreas y toda la tabla para volver a donde estaba. El elemento se guarda al
   * montar, no al cerrar, porque para entonces ya se lo llevó el `focus()` de
   * arriba.
   */
  useEffect(() => {
    const abridor = document.activeElement
    return () => {
      if (abridor instanceof HTMLElement && document.contains(abridor)) abridor.focus()
    }
  }, [])

  /**
   * ⭐⭐ EL FOCO NO SE ESCAPA DEL DIÁLOGO.
   *
   * `aria-modal="true"` le dice al lector de pantalla que lo de atrás no existe,
   * y **no hace nada con la tecla Tab**: sin esto, tabular desde el último
   * control del modal sigue por la página de atrás —que está tapada por el
   * overlay— y el operador queda escribiendo en controles que no ve. Es la mitad
   * que el `<dialog>` nativo daría gratis y que el docstring de arriba explica
   * por qué no se usa.
   *
   * La lista de focalizables se recalcula en cada Tab a propósito: el modal de
   * carga agrega y quita filas de archivos mientras está abierto, así que una
   * lista tomada al montar quedaría vieja.
   */
  useEffect(() => {
    const alTabular = (evento: KeyboardEvent) => {
      if (evento.key !== "Tab" || contenedor.current === null) return

      const focalizables = contenedor.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      if (focalizables.length === 0) {
        evento.preventDefault()
        contenedor.current.focus()
        return
      }

      const primero = focalizables[0]!
      const ultimo = focalizables[focalizables.length - 1]!
      const activo = document.activeElement

      if (evento.shiftKey && (activo === primero || activo === contenedor.current)) {
        evento.preventDefault()
        ultimo.focus()
      } else if (!evento.shiftKey && activo === ultimo) {
        evento.preventDefault()
        primero.focus()
      }
    }

    document.addEventListener("keydown", alTabular)
    return () => document.removeEventListener("keydown", alTabular)
  }, [])

  /**
   * Escape cierra. En `document` y no en el contenedor porque el foco puede
   * haberse ido a cualquier control de adentro.
   */
  useEffect(() => {
    const alPresionar = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") onCerrar()
    }
    document.addEventListener("keydown", alPresionar)
    return () => document.removeEventListener("keydown", alPresionar)
  }, [onCerrar])

  return (
    <div className="dialogo__fondo">
      <div
        className="dialogo"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialogo-titulo"
        tabIndex={-1}
        ref={contenedor}
      >
        <h2 id="dialogo-titulo" className="dialogo__titulo">
          {titulo}
        </h2>
        {children}
      </div>
    </div>
  )
}
